import {
  forwardRef,
  useImperativeHandle,
  useState,
  PropsWithChildren,
  useEffect,
  useRef,
} from 'react'
import { VariantProps, tv } from 'tailwind-variants'
import { ModalStatic } from './ModalStatic'

const modalV = tv({
  slots: {
    container: 'fixed inset-0 flex w-screen items-center justify-center p-4',
    backdrop:
      'fixed inset-0 bg-black/50 backdrop-blur-sm transition-all duration-75',
    panel: 'w-full max-w-[40rem] rounded-[1.25rem] ',
  },
  variants: {
    mode: {
      normal: {
        panel: 'bg-item-menu-sidebar',
      },
      blur: {
        panel: 'modal-auth-token-blur-bg',
      },
    },
  },
  defaultVariants: {
    mode: 'normal',
  },
})

export type TModalActionRef = {
  open(): void
  close(): void
}

type TModalProps = PropsWithChildren<
  {
    canDismiss?: boolean
    fullWidth?: boolean
    onOpen?: () => void
    onClose?: () => void
    panelClassName?: string
    containerClassName?: string
  } & VariantProps<typeof modalV>
>

const Modal = forwardRef<TModalActionRef, TModalProps>(
  (
    {
      children,
      canDismiss = true,
      mode,
      fullWidth = true,
      onOpen,
      onClose,
      panelClassName,
      containerClassName,
    },
    ref,
  ) => {
    const [show, toggleShow] = useState(false)
    const firstRender = useRef(true)

    useEffect(() => {
      if (firstRender.current) {
        firstRender.current = false
        return
      }
      if (show) {
        onOpen?.()
      } else {
        onClose?.()
      }
    }, [show, onOpen, onClose])

    useImperativeHandle(ref, () => ({
      open() {
        toggleShow(true)
      },
      close() {
        toggleShow(false)
      },
    }))

    return (
      <ModalStatic
        show={show}
        toggleShow={toggleShow}
        canDismiss={canDismiss}
        mode={mode}
        fullWidth={fullWidth}
        panelClassName={panelClassName}
        containerClassName={containerClassName}>
        {children}
      </ModalStatic>
    )
  },
)

export default Modal
